import { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import SearchableDropdown from "../components/SearchableDropdown";
import { SeasonWiseTransactionTable } from "../components/ReportTable";
import { fetchRegions, fetchParties, fetchSeasonWiseTransactionReport, fetchSeasons, fetchPaymentTypes } from "../services/seasonWiseTransactionService";
import { seasonWiseTransactionColumns } from "../config/reportColumns";
import ExcelExport from "../components/ExcelExport";

const SeasonWiseTransaction = ({ onLogout }) => {
  const [data, setData] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [seasonRange, setSeasonRange] = useState({});
  const [season, setSeason] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState([]);
  const [parties, setParties] = useState([]);
  const [party, setParty] = useState([]);
  const [selectedPackSizes, setSelectedPackSizes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentType, setPaymentType] = useState("");
  const [seasonYear, setSeasonYear] = useState("");
  const [dateError, setDateError] = useState("");

  const packSizeList = useMemo(() => [...new Set(data.map(d => d.pack_sizes).filter(Boolean))].sort().map(p => ({ label: p, value: p })), [data]);

  useEffect(() => {
    fetchSeasons()
      .then(res =>
        setSeasons(
          res.map(s => ({
            value: s.season_name,
            label: s.season_name,
            start_date: s.start_date,
            end_date: s.end_date
          }))
        )
      )
      .catch(console.error);
    fetchPaymentTypes().then(res => setPaymentTypes(res.map(p => ({ value: p, label: p })))).catch(console.error);
  }, []);

  useEffect(() => {
    fetchRegions().then(r => setRegions(r.map(reg => ({ value: reg, label: reg })))).catch(console.error);
  }, []);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    fetchParties(region.join(',')).then(res => setParties(res.map(p => ({ value: p, label: p })))).catch(console.error);
    setParty([]);
  }, [region, fromDate, toDate]);

  // NEW: Auto-load data when date range is complete and valid
  useEffect(() => {
    // Check if we have both dates, no error, and we're not already loading
    if (fromDate && toDate && !dateError && !loading) {
      autoLoadData(region, party);
    }

  }, [fromDate, toDate, dateError]); // Only re-run when dates or error status change

  const autoLoadData = async (reg = [], par = []) => {
    setLoading(true);
    try {
      const res = await fetchSeasonWiseTransactionReport({ 
        fromDate, 
        toDate, 
        region: reg.join(','), 
        party: par.join(','), 
        search: searchText, 
        paymentType 
      });
      setData(res.data || []);
    } catch (err) { 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(row => {
      const s = searchText.toLowerCase();
      const matchSearch = !searchText || row.products_name?.toLowerCase().includes(s) || row.pack_sizes?.toLowerCase().includes(s);
      const matchRegion = region.length === 0 || region.includes(row.region_name);
      const matchParty = party.length === 0 || party.includes(row.party_name);
      const matchPayment = !paymentType || row.cash_credit === paymentType;
      const matchPackSize = selectedPackSizes.length === 0 || selectedPackSizes.includes(row.pack_sizes);
      return matchSearch && matchRegion && matchParty && matchPayment && matchPackSize;
    });
  }, [data, searchText, region, party, paymentType, selectedPackSizes]);

  const formatDateForInput = (date) => {
    return formatDateForBangladesh(date);
  };

  const formatDateForBangladesh = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSeasonDateRange = (selectedSeason, selectedYear) => {
    if (!selectedSeason || !selectedYear) {
      return {};
    }

    const seasonObj = seasons.find(
      s => s.value === selectedSeason
    );

    if (!seasonObj) {
      return {};
    }

    const [startMonth, startDay] = seasonObj.start_date.split("-").map(Number);
    const [endMonth, endDay] = seasonObj.end_date.split("-").map(Number);

    let startYear = selectedYear;
    let endYear = selectedYear;

    if (startMonth > endMonth) {
      endYear = selectedYear + 1;
    }

    const minDate = new Date(
      `${startYear}-${String(startMonth).padStart(2, "0")}-${String(startDay).padStart(2, "0")}T00:00:00+06:00`
    );

    const maxDate = new Date(
      `${endYear}-${String(endMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}T23:59:59+06:00`
    );

    return {
      min: formatDateForBangladesh(minDate),
      max: formatDateForBangladesh(maxDate)
    };
  };

  const validateDateRange = (from, to) => {
    if (!from || !to) {
      return "";
    }

    const fromDateObj = new Date(`${from}T00:00:00+06:00`);
    const toDateObj = new Date(`${to}T23:59:59+06:00`);

    if (fromDateObj > toDateObj) {
      return "From date cannot be greater than To date.";
    }

    if (
      fromDateObj < new Date(seasonRange.min) ||
      fromDateObj > new Date(seasonRange.max)
    ) {
      return `From date must be between ${seasonRange.min} and ${seasonRange.max}`;
    }

    if (
      toDateObj < new Date(seasonRange.min) ||
      toDateObj > new Date(seasonRange.max)
    ) {
      return `To date must be between ${seasonRange.min} and ${seasonRange.max}`;
    }

    return "";
  };

  const handleFromDateChange = (value) => {
    if (!value) {
      setFromDate("");
      return;
    }

    const selectedYear = new Date(value).getFullYear();
    setSeasonYear(selectedYear);

    const range = getSeasonDateRange(season, selectedYear);
    setSeasonRange(range);

    if (value < range.min || value > range.max) {
      setDateError(
        `For ${season} season, date must be between ${range.min} and ${range.max}`
      );
      return;
    }

    setDateError("");
    setFromDate(value);
  };

  return (
    <DashboardLayout onLogout={onLogout}>
      <main className="p-2 bg-gray-100 h-screen overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold">Season Wise Sales Report</h1>
          <ExcelExport data={filteredData} columns={seasonWiseTransactionColumns} reportTitle="Season Wise Sales Report " fileName="Season_Wise_Sales_report.xlsx" />
        </div>
        <div className="bg-white p-2 rounded-lg shadow-sm mb-1 flex flex-wrap gap-2 items-end">
          <SearchableDropdown
            options={seasons}
            value={season}
            onChange={(value) => {
              setSeason(value);
              setFromDate("");
              setToDate("");
              setSeasonRange({});
              setDateError("");
              // Clear data when season changes
              setData([]);
            }}
            placeholder="Select Season"
          />
          {season &&
            <input
              type="date"
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
            />
          }
          {season &&
            <input
              type="date"
              value={toDate}
              min={seasonRange.min}
              max={seasonRange.max}
              onChange={(e) => {
                const value = e.target.value;

                if (value < seasonRange.min || value > seasonRange.max) {
                  setDateError(
                    `To date must be between ${seasonRange.min} and ${seasonRange.max}`
                  );
                  return;
                }

                if (fromDate && value < fromDate) {
                  setDateError(
                    "To date cannot be before From date"
                  );
                  return;
                }

                setDateError("");
                setToDate(value);
              }}
              className="border rounded px-2 py-1.5 text-sm"
            />
          }

          {dateError && (
            <div className="w-full text-red-600 text-sm font-medium mt-1">
              ⚠️ {dateError}
            </div>
          )}
          {fromDate && toDate && <SearchableDropdown options={regions} value={region} onChange={setRegion} placeholder="Select Region" isMulti={true} />}
          {fromDate && toDate && <SearchableDropdown options={parties} value={party} onChange={setParty} placeholder="Select Party" isMulti={true} />}
          {fromDate && toDate && <SearchableDropdown options={packSizeList} value={selectedPackSizes} onChange={setSelectedPackSizes} placeholder="Pack Size" isMulti={true} />}
          <SearchableDropdown options={paymentTypes} value={paymentType} onChange={setPaymentType} placeholder="Select Payment" />
          
          {/* Apply button is now optional - can keep it for manual refresh */}
          <button
            disabled={!!dateError}
            onClick={() => autoLoadData(region, party)}
            className={`px-4 py-1.5 rounded-md text-sm text-white ${dateError
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>
        <SeasonWiseTransactionTable reportData={filteredData.slice(0, 200)} />
      </main>
    </DashboardLayout>
  );
};

export default SeasonWiseTransaction;